import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DigitsNumAccordingToType, MfaType } from '../../../generic-components/MFA/types';
import { useStore } from '../../../store';
import { useCountdownTimer } from '../../../hooks/utilities/useCountdownTimer';
import { VIEWS_MAP } from '../../../router/constants';
import { postMfaVerification } from '../api/postMfaVerification';
import { postMfaResend } from '../api/postMfaResend';
import { NavigationHeader } from '../components/NavigationHeader';
import { getAnalyticsFlowNameForSuccess, MoveMoneySteps, MoveMoneyTypes } from '../consts';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { CodeValidationWrapper } from '../components/mfa/CodeValidationWrapper';
import { ResendCodeWrapper } from '../components/mfa/ResendCodeWrapper';

export const MoveMoneyMfaStep = () => {
  const [resendType, setResendType] = useState<MfaType>(MfaType.SMS);
  const [pendingResendType, setPendingResendType] = useState<MfaType>(MfaType.SMS);
  const [isResendCode, setIsResendCode] = useState<boolean>(false);
  const numberOfDigits = useMemo(
    () => (resendType === MfaType.SMS ? DigitsNumAccordingToType.SMS : DigitsNumAccordingToType.VOICE),
    [resendType],
  );
  const moveMoneyData = useStore((state) => state.moveMoneyData);
  const setMoveMoneyData = useStore((state) => state.setMoveMoneyData);
  const { startTimer, timeLeft } = useCountdownTimer({
    intervalDuration: 30,
    onFinishCountdown: () => {},
  });
  const resetMoveMoneyData = useStore((state) => state.resetMoveMoneyData);
  const navigate = useNavigate();
  const { moveType } = useParams();
  const postMfaAttempts = useRef<number>(0);

  /** Queries */
  const { mutateAsync: postWireMfaVerify, isLoading, error } = postMfaVerification();
  const { mutateAsync: postMfaResendCode, isLoading: isLoadingResendMfa } = postMfaResend();

  const handleResend = async () => {
    setResendType(pendingResendType);
    trackMixPanel('Act on MFA Resend', { Method: pendingResendType });
    await postMfaResendCode({ mfaData: { ...moveMoneyData.mfaChallengeData!, mfaType: pendingResendType } });
    setIsResendCode(false);
    startTimer();
  };

  const handleMfaCode = async (code: string) => {
    postMfaAttempts.current += 1;
    try {
      await postWireMfaVerify({
        answer: code,
        mfaData: moveMoneyData.mfaChallengeData,
        billId: moveMoneyData.billId,
      });
      setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.SUCCESS });
      trackMixPanel('Result of MFA for Transaction', {
        Status: 'Success',
        'Attempt number': postMfaAttempts.current,
      });
    } catch (e) {
      trackMixPanel('Result of MFA for Transaction', {
        Status: 'Failed',
        'Attempt number': postMfaAttempts.current,
      });
    }
  };

  const handleCloseFlow = () => {
    resetMoveMoneyData();
    navigate(`/${VIEWS_MAP.DASHBOARD}`);
  };

  const handleBack = () => {
    setPendingResendType(resendType);
    setIsResendCode(false);
  };

  useEffect(() => {
    trackMixPanel('View MFA for Transaction Screen ', {
      Origin: getAnalyticsFlowNameForSuccess(moveType as MoveMoneyTypes),
    });
  }, []);

  return (
    <div className='absolute left-0 top-0 w-full h-full bg-bw00 z-50 flex flex-col items-center '>
      <NavigationHeader hideBackButton onBack={() => {}} onClose={handleCloseFlow} />
      {!isResendCode && (
        <CodeValidationWrapper
          mfaDestination={moveMoneyData?.mfaChallengeData?.mfaDestination || ''}
          isLoading={isLoading}
          isError={!!error}
          numberOfDigits={numberOfDigits}
          timeLeft={timeLeft || 0}
          onFulfill={handleMfaCode}
          onResendCode={() => setIsResendCode(true)}
        />
      )}
      {isResendCode && (
        <ResendCodeWrapper
          isLoading={isLoadingResendMfa}
          onResendType={(type) => setPendingResendType(type)}
          pendingResendType={pendingResendType}
          handleResend={handleResend}
          handleBack={handleBack}
        />
      )}
    </div>
  );
};
